import React, { memo, useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import Icon from '@/components/redesign/icons/Icons';
import Logo from '@/components/redesign/Logo';
import { MemoizedChatMessage } from './MemoizedChatMessage';
import { useAuth } from '@/contexts/AuthContext';
import { useChat } from '@/contexts/ChatContext';
import { DEFAULT_MODEL, HUMAN_MODE } from '@/libs/app/const';
import { canUseModel, isKnownChatModel } from '@/libs/app/models';
import { throttle } from '@/libs/data/throttle';
import { getPubMed } from '@/libs/request';
import { t } from 'i18next';

const VetPlaceholder = dynamic(() => import('../placeholder/VetPlaceHolder'));

const HumanPlaceholder = dynamic(() =>
  import('../placeholder/HumanPlaceHolder'),
);
const TemperatureSlider = dynamic(() =>
  import('./Temperature').then((module) => module.TemperatureSlider),
);
const ErrorMessageDiv = dynamic(() =>
  import('./ErrorMessageDiv').then((module) => module.ErrorMessageDiv),
);
const ChatLoader = dynamic(() =>
  import('./ChatLoader').then((module) => module.ChatLoader),
);
const ChatInput = dynamic(() =>
  import('./ChatInput').then((module) => module.ChatInput),
);

export const Chat = memo(({ handleSend, stopConversationRef }) => {
  const {
    state: {
      selectedConversation,
      modelError,
      loading,
      actionsDisabled,
    },
    systemPrompt,
    setSystemPrompt,
    currentMessage,
    setCurrentMessage,
    handleUpdateConversation,
    dispatch: homeDispatch,
    handleSaveConversation,
  } = useChat();
  const { currentUser } = useAuth();
  const [content, setContent] = useState('');
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showScrollDownButton, setShowScrollDownButton] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!selectedConversation || !currentUser) return;
    // Keep compatible legacy IDs in storage; normalize only labels/selections.
    if (
      !isKnownChatModel(selectedConversation.model) ||
      !canUseModel(selectedConversation.model, currentUser)
    ) {
      handleUpdateConversation(selectedConversation, {
        key: 'model',
        value: DEFAULT_MODEL,
      });
    }
  }, [selectedConversation, handleUpdateConversation, currentUser]);

  const sendPubMedRequest = async (pubmedMessage, deleteCount = 0) => {
    if (selectedConversation) {
      let updatedConversation;
      if (deleteCount) {
        const updatedMessages = [...selectedConversation.messages];
        for (let i = 0; i < deleteCount; i++) {
          updatedMessages.pop();
        }
        updatedConversation = {
          ...selectedConversation,
          messages: [...updatedMessages, pubmedMessage],
          followups: [],
        };
      } else {
        updatedConversation = {
          ...selectedConversation,
          messages: [...selectedConversation.messages, pubmedMessage],
          followups: [],
        };
      }
      homeDispatch({
        field: 'selectedConversation',
        value: updatedConversation,
      });
      homeDispatch({ field: 'loading', value: true });
      homeDispatch({ field: 'messageIsStreaming', value: true });
      const pubMedBody = {
        messages: updatedConversation.messages.filter(
          (message) =>
            message.role !== 'assistant' || message.type !== 'pubmed',
        ),
        temperature: updatedConversation.temperature,
        default_lang: t('lang'),
      };

      const response = await getPubMed(pubMedBody);

      if (!response.ok) {
        homeDispatch({ field: 'loading', value: false });
        homeDispatch({ field: 'messageIsStreaming', value: false });
        return;
      }
      const articles = await response.json();
      homeDispatch({ field: 'loading', value: false });
      homeDispatch({ field: 'messageIsStreaming', value: false });
      if (!articles || articles.length === 0) {
        return;
      }

      const updatedMessages = [
        ...updatedConversation.messages,
        { role: 'assistant', articles, type: 'pubmed' },
      ];
      updatedConversation = {
        ...updatedConversation,
        messages: updatedMessages,
      };
      homeDispatch({
        field: 'selectedConversation',
        value: updatedConversation,
      });
      await handleSaveConversation(updatedConversation);
    }
  };

  const handleScroll = () => {
    if (chatContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        chatContainerRef.current;
      const bottomTolerance = 30;

      if (scrollTop + clientHeight < scrollHeight - bottomTolerance) {
        setAutoScrollEnabled(false);
        setShowScrollDownButton(true);
      } else {
        setAutoScrollEnabled(true);
        setShowScrollDownButton(false);
      }
    }
  };

  const handleScrollDown = () => {
    chatContainerRef.current?.scrollTo({
      top: chatContainerRef.current.scrollHeight,
      behavior: 'smooth',
    });
  };

  const handleSettings = () => {
    setShowSettings(!showSettings);
  };

  const onClearAll = () => {
    if (
      confirm(t('Are you sure you want to clear all messages?')) &&
      selectedConversation
    ) {
      handleUpdateConversation(selectedConversation, {
        key: 'messages',
        value: [],
      });
    }
  };

  const scrollDown = () => {
    if (autoScrollEnabled) {
      messagesEndRef.current?.scrollIntoView(true);
    }
  };
  const throttledScrollDown = throttle(scrollDown, 250);

  useEffect(() => {
    throttledScrollDown();
    selectedConversation &&
      setCurrentMessage(
        selectedConversation.messages[selectedConversation.messages.length - 2],
      );
  }, [selectedConversation, throttledScrollDown, setCurrentMessage]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setAutoScrollEnabled(entry.isIntersecting);
        if (entry.isIntersecting) {
          textareaRef.current?.focus();
        }
      },
      {
        root: null,
        threshold: 0.5,
      },
    );
    const messagesEndElement = messagesEndRef.current;
    if (messagesEndElement) {
      observer.observe(messagesEndElement);
    }
    return () => {
      if (messagesEndElement) {
        observer.unobserve(messagesEndElement);
      }
    };
  }, [messagesEndRef]);

  return (
    <div className="relative flex flex-1 flex-col overflow-hidden bg-white">
      {modelError ? (
        <ErrorMessageDiv error={modelError} />
      ) : (
        <>
          <div
            className="flex-1 overflow-x-hidden overflow-y-auto"
            ref={chatContainerRef}
            onScroll={handleScroll}
          >
            {selectedConversation?.messages.length === 0 ? (
              <>
                {/* Empty state */}
                <div className="mx-auto flex max-w-[600px] flex-col items-center px-6 pt-16">
                  <Logo size={32} />
                  <h2 className="t-display mt-4 text-[24px] text-ink-900">
                    {t('New consultation')}
                  </h2>
                  <p className="mt-2 text-center text-sm text-ink-500">
                    {t(
                      'Ask a clinical question, paste a chart excerpt, or describe a presentation.',
                    )}
                  </p>

                  {/* Settings card */}
                  <div className="mt-8 w-full rounded-xl border border-border bg-white p-5">
                    <button
                      onClick={handleSettings}
                      className="flex w-full items-center justify-between text-left"
                    >
                      <span className="text-sm font-semibold text-ink-900">
                        {t('System Instructions')} &amp; {t('Temperature')}
                      </span>
                      <Icon.Chevron
                        width={14}
                        height={14}
                        className={`text-ink-400 transition-transform ${showSettings ? 'rotate-180' : ''}`}
                      />
                    </button>

                    {showSettings && (
                      <div className="mt-4 space-y-4">
                        <div>
                          <label className="mb-1.5 block text-xs font-semibold text-ink-700">
                            {t('System Instructions')}
                          </label>
                          <textarea
                            value={systemPrompt}
                            onChange={(e) => setSystemPrompt(e.target.value)}
                            className="h-32 w-full resize-none rounded-md border border-border bg-white px-3 py-2 text-sm text-ink-900 placeholder:text-ink-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                            placeholder={t('Enter system instructions...')}
                          />
                        </div>
                        <TemperatureSlider
                          label={t('Temperature')}
                          onChangeTemperature={(temperature) =>
                            handleUpdateConversation(selectedConversation, {
                              key: 'temperature',
                              value: temperature,
                            })
                          }
                        />
                      </div>
                    )}
                  </div>

                  {/* Placeholders */}
                  {selectedConversation.model === DEFAULT_MODEL && (
                    <div className="mt-6 w-full">
                      {selectedConversation.mode === HUMAN_MODE ? (
                        <HumanPlaceholder
                          setQuery={(content) => {
                            const message = {
                              role: 'user',
                              content,
                              type: 'chat',
                            };
                            setCurrentMessage(message);
                            handleSend(message, 0);
                            setContent('');
                          }}
                        />
                      ) : (
                        <VetPlaceholder
                          setQuery={(content) => {
                            const message = {
                              role: 'user',
                              content,
                              type: 'chat',
                            };
                            setCurrentMessage(message);
                            handleSend(message, 0);
                            setContent('');
                          }}
                        />
                      )}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Settings toggle + clear — visible when conversation has messages */}
                <div className="flex items-center justify-end gap-3 px-4 pt-2">
                  <button
                    onClick={onClearAll}
                    disabled={actionsDisabled}
                    className="flex items-center gap-1 text-[11.5px] text-ink-500 hover:text-status-red-500 disabled:opacity-40"
                  >
                    <Icon.Trash width={12} height={12} />
                    {t('Clear')}
                  </button>
                  <button
                    onClick={handleSettings}
                    className="flex items-center gap-1 text-[11.5px] text-ink-500 hover:text-ink-800"
                  >
                    <Icon.Settings width={12} height={12} />
                    {showSettings ? t('Hide settings') : t('Settings')}
                  </button>
                </div>

                {/* Settings inline panel (when messages exist) */}
                {showSettings && (
                  <div className="mx-auto max-w-[600px] border-b border-border p-5">
                    <div className="space-y-4">
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold text-ink-700">
                          {t('System Instructions')}
                        </label>
                        <textarea
                          value={systemPrompt}
                          onChange={(e) => setSystemPrompt(e.target.value)}
                          className="h-32 w-full resize-none rounded-md border border-border bg-white px-3 py-2 text-sm text-ink-900 placeholder:text-ink-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                          placeholder={t('Enter system instructions...')}
                        />
                      </div>
                      <TemperatureSlider
                        label={t('Temperature')}
                        onChangeTemperature={(temperature) =>
                          handleUpdateConversation(selectedConversation, {
                            key: 'temperature',
                            value: temperature,
                          })
                        }
                      />
                    </div>
                  </div>
                )}

                {/* Messages */}
                {selectedConversation?.messages.map((message, index) => (
                  <MemoizedChatMessage
                    key={index}
                    message={message}
                    messageIndex={index}
                    handleSend={handleSend}
                    onEdit={(editedMessage) => {
                      setCurrentMessage(editedMessage);
                      if (
                        editedMessage.type === 'pubmed' &&
                        selectedConversation.model === DEFAULT_MODEL
                      ) {
                        sendPubMedRequest(
                          editedMessage,
                          selectedConversation?.messages.length - index,
                        );
                      } else {
                        if (editedMessage.type === 'pubmed') {
                          setCurrentMessage({
                            ...editedMessage,
                            type: 'chat',
                          });
                        }
                        handleSend(
                          editedMessage,
                          selectedConversation?.messages.length - index,
                        );
                      }
                    }}
                    onPubMedRef={(msg) => {
                      setCurrentMessage(msg);
                      sendPubMedRequest(msg, 0);
                    }}
                  />
                ))}

                {loading && <ChatLoader />}

                <div className="h-[162px] bg-white" ref={messagesEndRef} />
              </>
            )}
          </div>

          <ChatInput
            stopConversationRef={stopConversationRef}
            textareaRef={textareaRef}
            content={content}
            setContent={setContent}
            onSend={(message) => {
              setCurrentMessage(message);
              return handleSend(message, 0);
            }}
            onScrollDownClick={handleScrollDown}
            onRegenerate={() => {
              if (actionsDisabled) {
                return;
              }
              if (currentMessage) {
                if (
                  currentMessage.type === 'pubmed' &&
                  selectedConversation.model === DEFAULT_MODEL
                ) {
                  sendPubMedRequest(currentMessage, 2);
                } else {
                  if (currentMessage.type === 'pubmed') {
                    setCurrentMessage((msg) => ({ ...msg, type: 'chat' }));
                  }
                  handleSend(currentMessage, 2);
                }
              }
            }}
            showScrollDownButton={showScrollDownButton}
          />
        </>
      )}
    </div>
  );
});
Chat.displayName = 'Chat';
